import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import {
  matchValidator,
  minValueValidator,
  requiredValidator,
} from "../../helpers/validators";
import { showSuccess } from "../../components/notification/toastr-actions";
import {
  FeatureCard,
  FeatureCardColumn,
  FeatureCardRow,
  FeatureCardTitle,
} from "../../styles/shared/card";
import TextInput from "../../components/controls/text-input";
import Checkbox from "../../components/controls/checkbox";
import Button from "../../components/controls/button";
import styled from "styled-components";
import { ApplicationSupplierProductDetail } from "../../models/supplier/application-supplier-product-detail";
import { ApplicationProductDetail } from "../../models/product/application-product-detail";
import { fetchProduct } from "../../store/features/product/product-slice";
import {
  fetchSupplierProduct,
  fetchSupplierProductComparisons,
  insertSupplierProduct,
  resetSearchProducts,
  resetComparisonProducts,
  resetSizeUnitUpdatedProducts,
  searchSupplierProducts,
  updateProductSizeUnit,
  updateSupplierProduct,
} from "../../store/features/supplier/supplier-product-slice";
import { ApplicationError } from "../../models/errors/application-error";
import { ApplicationProduct } from "../../models/product/application-product";
import Alert from "@mui/material/Alert";
import {
  SmallTextContainer,
  UnderlinedTextContainer,
} from "../../styles/shared/table";
import SizeInput from "../../components/controls/size-input";
import { ApplicationSizeUnit } from "../../models/product/application-size-unit";
import SearchDropdown from "../../components/controls/search-dropdown";
import {
  ProductContainer,
  ProductImage,
  ProductNameContainer,
  ProductTitle,
} from "../../styles/shared/product";
import CurrencyInput from "../../components/controls/currency-input";
import { currencyFormatter } from "../../helpers/formatters";
import Image from "../../components/controls/image";
import { closeDrawer } from "../../store/features/drawer/drawer-slice";
import { ButtonWrapper, FieldWrapper, InfoContainer, TextInputWrapper } from "../../styles/shared/product-information";
import { ICON_ID } from "../../components/icons/google-icon";
import NumberInput from "../../components/controls/number-input";
import { ApplicationSupplierProductHeader } from "../../models/supplier/application-supplier-product-header";

interface SupplierProductModalProps {
  id: number;
}

interface Validation {
  name: string | null;
  size: string | null;
  sizeUnit: string | null;
  price: string | null;
  minimumOrderQuantity: string | null;
}

interface DynamicFieldValidation {
  dynamicFields: DynamicFieldErrors;
}

interface DynamicField {
  key: string | null;
  value: string | null;
}

interface DynamicFieldErrors {
  [index: number]: {
    key: string | null;
    value: string | null;
  };
}

export default function SupplierProductDrawer({
  id,
}: SupplierProductModalProps) {
  const dispatch = useAppDispatch();
  const supplierProductState = useAppSelector(
    (state) => state.supplierProducts
  );
  const supplierState = useAppSelector((state) => state.supplier);
  const comparisonState = useAppSelector((state) => state.supplierProducts);
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [comparisonPosition, setComparisonPosition] = useState({ top: 0, left: 0 });
  const [comparisonAnchorRect, setComparisonAnchorRect] = useState<DOMRect | null>(null);
  const [validation, setValidation] = useState<Validation>({
    name: null,
    size: null,
    sizeUnit: null,
    price: null,
    minimumOrderQuantity: null,
  });

  const [product, setProduct] = useState<ApplicationSupplierProductDetail>({
    id: 0,
    reference: "",
    supplierId: supplierState.selectedSupplier?.id ?? 0,
    supplierName: supplierState.selectedSupplier?.name ?? "",
    name: "",
    productId: null,
    description: null,
    image: "",
    size: 0,
    sizeUnit: ApplicationSizeUnit.KG,
    plu: "",
    updated: new Date(),
    origin: "",
    active: true,
    price: null,
    openPricing: false,
    minimumOrderQuantity: null,
    hasHistory: false,
    created: new Date(),
    information: {}
    ,layerSize: null,
    palletSize: null
  });

  const [mappedProduct, setMappedProduct] = useState<ApplicationProductDetail>({
    id: 0,
    departmentId: 0,
    subDepartmentId: 0,
    name: "",
    description: "",
    image: "",
    plu: "",
    active: true,
    information: {},
    suggestedQuantity: 0,
    branches: [],
    vatId: 0
  });

  const loadProductMapping = (productId: number) => {
    if (productId > 0) {
      dispatch(fetchProduct(productId)).then((response) => {
        var product = response.payload as ApplicationProductDetail;
        setMappedProduct(product);
      });
    }
  };

  useEffect(() => {
    if (id)
      dispatch(fetchSupplierProduct(id)).then((response) => {
        const product = response.payload as ApplicationSupplierProductDetail;
        if (product) {
          setProduct(product);
          loadProductMapping(product?.productId ?? 0);
        }
      });

    return () => {
      dispatch(resetSizeUnitUpdatedProducts());
    };
  }, [id]);

  useEffect(() => {
    if (product.price && product.sizeUnit && product.size) {
      setUnitPrice(product.price / product.size);
    }
  }, [product.size, product.sizeUnit, product.price]);

  useEffect(() => {
    return () => {
      dispatch(resetComparisonProducts());
    };
  }, [dispatch]);

  const closeComparison = () => {
    setComparisonOpen(false);
    setComparisonAnchorRect(null);
    dispatch(resetComparisonProducts());
  };

  const updateComparisonPosition = (anchorRect: DOMRect, estimatedHeight: number) => {
    const viewportPadding = 12;
    const gap = 8;
    const popoverWidth = 720;

    const spaceBelow = window.innerHeight - anchorRect.bottom - viewportPadding;
    const spaceAbove = anchorRect.top - viewportPadding;
    const renderAbove = spaceBelow < estimatedHeight && spaceAbove >= estimatedHeight;

    const top = renderAbove
      ? Math.max(viewportPadding, anchorRect.top - estimatedHeight - gap)
      : Math.min(anchorRect.bottom + gap, window.innerHeight - estimatedHeight - viewportPadding);

    const idealLeft = anchorRect.left;
    const maxLeft = window.innerWidth - popoverWidth - viewportPadding;

    setComparisonPosition({
      top: Math.max(viewportPadding, top),
      left: Math.max(viewportPadding, Math.min(idealLeft, maxLeft)),
    });
  };

  useEffect(() => {
    if (!comparisonOpen || !comparisonAnchorRect) {
      return;
    }

    const estimatedHeight = comparisonState.comparisonLoading
      ? 170
      : 96 + Math.max(1, comparisonState.comparisonProducts?.length ?? 0) * 34;

    updateComparisonPosition(comparisonAnchorRect, estimatedHeight);
  }, [comparisonOpen, comparisonAnchorRect, comparisonState.comparisonLoading, comparisonState.comparisonProducts]);

  useEffect(() => {
    if (!comparisonOpen) {
      return;
    }

    const handleScroll = () => {
      closeComparison();
    };

    document.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [comparisonOpen]);

  const onChange = <K extends keyof ApplicationSupplierProductDetail>(
    value: ApplicationSupplierProductDetail[K],
    property: K,
    validation?: (input: ApplicationSupplierProductDetail[K]) => string | null
  ) => {
    const errorMessage = validation ? validation(value) : null;
    setProduct((prevState) => ({
      ...prevState,
      [property]: value,
    }));

    setValidation((prevState) => ({
      ...prevState,
      [property]: errorMessage,
    }));
  };

  const fetchProducts = async (query: string): Promise<void> => {
    if (!supplierState.selectedSupplier) {
      throw new ApplicationError("No selected supplier");
    }

    await dispatch(
      searchSupplierProducts({
        supplierId: supplierState.selectedSupplier?.id,
        searchText: query,
        fetchAll: true,
      })
    );
  };

  const isProductEqual = (
    option: ApplicationProduct,
    value: ApplicationProduct
  ) => option.id == value.id;

  const renderProductOption = (
    props: React.HTMLProps<HTMLLIElement>,
    option: ApplicationProduct
  ) => (
    <StyledOption {...props}>
      <OptionImage src={option.image} alt={option.name} />
      <OptionText>
        <OptionName>{option.name}</OptionName>
        <OptionSize>Plu: {option.plu}</OptionSize>
      </OptionText>
    </StyledOption>
  );

  const onAutocompleteChange = (value: ApplicationProduct | null) => {
    onChange(value?.id ?? null, "productId");
    loadProductMapping(value?.id ?? 0);
  };

  const updateProduct = async () => {
    if (!validateAndCheckErrors()) {
      return;
    }

    var notificationMessage = `${product.name} `;
    if (product.id > 0) {
      await dispatch(updateSupplierProduct(product));
      notificationMessage += "updated";
    } else {
      await dispatch(insertSupplierProduct(product));
      notificationMessage += "created";
    }

    dispatch(showSuccess(notificationMessage));
    dispatch(closeDrawer());
  };

  const validateAndCheckErrors = () => {
    const isPalletSupplier = supplierState.selectedSupplier?.productOrderingType === "Pallets";

    const minimumOrderQuantityError = isPalletSupplier && product.minimumOrderQuantity != null && product.minimumOrderQuantity > 0 &&
      product.layerSize != null && product.layerSize > 0 && product.minimumOrderQuantity % product.layerSize !== 0
      ? `Minimum Order Quantity must be a multiple of the Layer Size (${product.layerSize}).`
      : null;
    const validation = {
      name: requiredValidator(product.name),
      size: requiredValidator(product.size),
      price: minValueValidator(product.price, 0),
      sizeUnit: matchValidator(
        product.sizeUnit,
        mappedProduct?.supplierSizeUnit,
        true,
        `The size unit must be ${mappedProduct.supplierSizeUnit}`
      ),
      minimumOrderQuantity: minimumOrderQuantityError,
    };

    setValidation(validation);

    const hasErrors = Object.values(validation).some((error) => error !== null);

    if (hasErrors) {
      return false;
    } else {
      return true;
    }
  };

  const updateSupplierSizeUnits = () => {
    const newSizeUnit = product.sizeUnit;
    const productId = product.productId;

    if (productId) {
      dispatch(updateProductSizeUnit({ productId, newSizeUnit })).then(() => {
        loadProductMapping(productId);
      });
      validation.sizeUnit = null;
      setValidation(validation);
    }
  };

  const openComparison = (event: React.MouseEvent<HTMLButtonElement>) => {
    const productId = product.productId;

    if (!productId) {
      return;
    }

    const anchorRect = event.currentTarget.getBoundingClientRect();
    setComparisonOpen(true);
    setComparisonAnchorRect(anchorRect);
    updateComparisonPosition(anchorRect, 120);
    dispatch(resetComparisonProducts());
    void dispatch(fetchSupplierProductComparisons(productId));
  };


  const handleAddInfo = () => {
    setProduct(prev => ({
      ...prev,
      information: {
        ...prev.information,
        "": "" // add an empty key/value pair
      }
    }));
  };

  const handleRemoveInfo = (key: string) => {
    setProduct(prev => {
      const newInformation = { ...prev.information };
      delete newInformation[key];
      return { ...prev, information: newInformation };
    });
  };
  const handleInfoChange = (oldKey: string, newKey: string, value: string) => {
    setProduct(prev => {
      const newInformation = { ...prev.information };

      // remove the old key if it changed
      if (oldKey !== newKey) {
        delete newInformation[oldKey];
      }

      newInformation[newKey] = value;
      return { ...prev, information: newInformation };
    });
  };
  return (
    <Container>
      {validation.sizeUnit && (
        <Alert
          sx={{
            marginBottom: "10px",
          }}
          variant="outlined"
          severity="error"
        >
          The mapped product '{mappedProduct.name}' is purchased in units of '
          {mappedProduct.supplierSizeUnit}' from other suppliers. To
          standardise, update all other suppliers to use '{product.sizeUnit}'.
          <UnderlinedTextContainer onClick={updateSupplierSizeUnits}>
            Click here to automatically update other suppliers
          </UnderlinedTextContainer>
        </Alert>
      )}
      {supplierProductState.sizeUnitUpdatedProducts && (
        <Alert
          sx={{
            marginBottom: "10px",
          }}
          variant="outlined"
          severity="success"
          onClose={() => dispatch(resetSizeUnitUpdatedProducts())}
        >
          The following suppliers product have been updated to use the new size
          unit '{product.sizeUnit}'.
          {supplierProductState.sizeUnitUpdatedProducts.map((x) => (
            <div>
              <strong>{x.supplierName}</strong> - {x.name}{" "}
            </div>
          ))}
        </Alert>
      )}

      <Body>
        <Active>
          <Checkbox
            id="cbActive"
            checked={product.active}
            onChange={(event) => onChange(event.target.checked, "active")}
            label="Active"
          ></Checkbox>
        </Active>
        <FeatureCard>
          <FeatureCardTitle>Product information</FeatureCardTitle>
          <FeatureCardRow>
            <FeatureCardColumn>
              <TextInput
                id="txtProductName"
                value={product.name}
                label="Name"
                onChange={(event) =>
                  onChange(event.target.value, "name", (input) =>
                    requiredValidator(input)
                  )
                }
                error={validation.name}
              ></TextInput>
            </FeatureCardColumn>
            <FeatureCardColumn>
              <SizeInput
                id="supplier-product-size"
                label="Size"
                valueNumber={product.size}
                valueUnit={product.sizeUnit}
                onChange={(newValue: {
                  number: number;
                  unit: ApplicationSizeUnit;
                }) => {
                  onChange(newValue.number, "size", (input) =>
                    requiredValidator(input)
                  );
                  onChange(newValue.unit, "sizeUnit", (input) =>
                    matchValidator(
                      input,
                      mappedProduct?.supplierSizeUnit,
                      true,
                      `The size unit must be ${mappedProduct.supplierSizeUnit}`
                    )
                  );
                  dispatch(resetSizeUnitUpdatedProducts());
                }}
                error={validation.size ?? validation.sizeUnit}
              />
            </FeatureCardColumn>
          </FeatureCardRow>
          <FeatureCardRow>
            <FeatureCardColumn>
              <TextInput
                id="txtReference"
                value={product.reference}
                label="Supplier reference"
                onChange={(event) => onChange(event.target.value, "reference")}
              ></TextInput>
            </FeatureCardColumn>
            <FeatureCardColumn>
              <TextInput
                id="txtOrigin"
                value={product.origin}
                label="Origin"
                onChange={(event) => onChange(event.target.value, "origin")}
              ></TextInput>
            </FeatureCardColumn>

          </FeatureCardRow>
        </FeatureCard>
        <FeatureCard>
          <FeatureCardTitle>Mapping</FeatureCardTitle>
          <FeatureCardRow>
            <FeatureCardColumn>
              <SearchDropdown<ApplicationProduct>
                fetchOptions={fetchProducts}
                clearOptions={() => dispatch(resetSearchProducts())}
                options={supplierProductState.searchProducts?.products ?? []}
                label="Search products"
                onChange={onAutocompleteChange}
                isOptionEqualToValue={isProductEqual}
                renderOption={renderProductOption}
              />
            </FeatureCardColumn>
          </FeatureCardRow>

          <FeatureCardRow>
            <FeatureCardColumn>
              {mappedProduct.id > 0 && (
                <MappedProduct>
                  <ProductContainer>
                    <ProductImage>
                      <Image image={mappedProduct.image}></Image>
                    </ProductImage>
                    <ProductNameContainer>
                      <ProductTitle>{mappedProduct.name}</ProductTitle>
                      <div>{mappedProduct.description} </div>
                      <SmallTextContainer>
                        Plu: {mappedProduct.plu}
                      </SmallTextContainer>
                    </ProductNameContainer>
                  </ProductContainer>
                </MappedProduct>
              )}
            </FeatureCardColumn>
          </FeatureCardRow>
        </FeatureCard>
        <FeatureCard>
          <FeatureCardTitle>Product Information</FeatureCardTitle>
          <InfoContainer>
            {Object.entries(product.information).map(([key, value], index) => (
              <FieldWrapper key={index}>
                <TextInputWrapper>
                  <TextInput
                    id={`info-key-${index}`}
                    label="Key"
                    value={key}
                    onChange={(e) =>
                      handleInfoChange(key, e.target.value, value)
                    }
                  />
                </TextInputWrapper>
                <TextInputWrapper>
                  <TextInput
                    id={`info-value-${index}`}
                    label="Value"
                    value={value}
                    onChange={(e) =>
                      handleInfoChange(key, key, e.target.value)
                    }
                  />
                </TextInputWrapper>
                <Button
                  variant="text"
                  label=""
                  id={`btnRemove-${index}`}
                  icon={ICON_ID.Close}
                  onClick={() => handleRemoveInfo(key)}
                />
              </FieldWrapper>
            ))}
          </InfoContainer>

          <ButtonWrapper>
            <Button
              label="Add"
              variant="text"
              id="BtnAdd"
              icon={ICON_ID.Add}
              onClick={handleAddInfo}
            />
          </ButtonWrapper>
        </FeatureCard>
                  {supplierState.selectedSupplier?.productOrderingType === "Pallets" && (
        <FeatureCard>
          <FeatureCardTitle>Pallet Configuration</FeatureCardTitle>
            <Spacer/>
            <FeatureCardRow>
              <FeatureCardColumn>
                <NumberInput
                  id="txtLayerSize"
                  label="Layer Size"
                  value={product.layerSize}
                  onChange={(value) => onChange(value, "layerSize")}
                />
                <FieldHint>Number of cases in one complete pallet layer.</FieldHint>
              </FeatureCardColumn>
              <FeatureCardColumn>
                <NumberInput
                  id="txtPalletSize"
                  label="Pallet Size"
                  value={product.palletSize}
                  onChange={(value) => onChange(value, "palletSize")}
                />
                <FieldHint>Number of layers in one full pallet.</FieldHint>
              </FeatureCardColumn>
              <FeatureCardColumn>
                <ReadOnlyValue>
                  <span>Full Pallet Quantity</span>
                  <strong>{product.layerSize && product.palletSize ? `${product.layerSize * product.palletSize} cases` : "Not configured"}</strong>
                </ReadOnlyValue>
                <FieldHint>Calculated as Layer Size x Pallet Size.</FieldHint>
              </FeatureCardColumn>
            </FeatureCardRow>
        </FeatureCard>
           )}
        <FeatureCard>
          <FeatureCardTitle>Price</FeatureCardTitle>
           <ComparisonActionRow>
                <Button
                  id="btnCompareSupplierPrices"
                  label="Compare supplier prices"
                  variant="outlined"
                  icon={ICON_ID.ViewList}
                  isSecondary={true}
                  disabled={!product.productId}
                  onClick={openComparison}
                />
                {comparisonOpen && typeof document !== "undefined"
                  ? createPortal(
                      <ComparisonTooltip $top={comparisonPosition.top} $left={comparisonPosition.left}>
                        <ComparisonTooltipHeader>
                          <ComparisonTooltipTitle>
                            {mappedProduct.name || product.name || "Product"} price comparison
                          </ComparisonTooltipTitle>
                          <Button
                            variant="text"
                            id="btnCloseComparison"
                            label=""
                            icon={ICON_ID.Close}
                            isNeutral={true}
                            fullWidth={false}
                            onClick={closeComparison}
                          />
                        </ComparisonTooltipHeader>

                        {comparisonState.comparisonLoading ? (
                          <ComparisonLoading>
                            <SmallTextContainer>Loading comparisons...</SmallTextContainer>
                          </ComparisonLoading>
                        ) : (
                          <ComparisonGrid>
                            <ComparisonGridHeader>Supplier</ComparisonGridHeader>
                            <ComparisonGridHeader>Price</ComparisonGridHeader>
                            <ComparisonGridHeader>Size</ComparisonGridHeader>
                            <ComparisonGridHeader>Description</ComparisonGridHeader>
                            {(comparisonState.comparisonProducts ?? []).map((comparisonRow: ApplicationSupplierProductHeader) => {
                              const isCurrentSupplier = comparisonRow.supplierId === product.supplierId;

                              return (
                                <ComparisonGridRow key={`${comparisonRow.id}`}>
                                  <ComparisonGridValue $isCurrentSupplier={isCurrentSupplier}>
                                    {comparisonRow.supplierName || "-"}
                                  </ComparisonGridValue>
                                  <ComparisonGridValue $isCurrentSupplier={isCurrentSupplier}>
                                    {comparisonRow.openPricing ? "TBC" : comparisonRow.price != null ? currencyFormatter.format(comparisonRow.price) : "-"}
                                  </ComparisonGridValue>
                                  <ComparisonGridValue $isCurrentSupplier={isCurrentSupplier}>
                                    {comparisonRow.size != null
                                      ? `${comparisonRow.size} ${comparisonRow.sizeUnit ?? ""}`.trim()
                                      : "-"}
                                  </ComparisonGridValue>
                                  <ComparisonGridValue $isCurrentSupplier={isCurrentSupplier}>
                                    {comparisonRow.description || "-"}
                                  </ComparisonGridValue>
                                </ComparisonGridRow>
                              );
                            })}
                          </ComparisonGrid>
                        )}
                      </ComparisonTooltip>,
                      document.body
                    )
                  : null}
              </ComparisonActionRow>
          <FeatureCardRow>
            
            <FeatureCardColumn>
             
              <Checkbox
                id="cbActive"
                checked={product.openPricing}
                onChange={(event) => {
                  onChange(0, "price");
                  onChange(event.target.checked, "openPricing")
                }}
                label="Open Pricing"
              ></Checkbox>
              <CurrencyInput
                disabled={product.openPricing}
                value={product.price}
                onChange={(event) => {
                  const val = parseFloat(event.target.value);
                  if (!isNaN(val)) {
                    onChange(val, "price", (input) => minValueValidator(input, 0));
                  } else if (event.target.value === "") {
                    onChange(null, "price", (input) => minValueValidator(input, 0)); // Handle empty string
                  }
                }}
                error={!!validation.price}
                helperText={validation.price}
              ></CurrencyInput>
              <Alert severity="info">
                <strong>{currencyFormatter.format(unitPrice)}</strong> per{" "}
                {product.sizeUnit}
              </Alert>
            </FeatureCardColumn>
            <FeatureCardColumn>
              <Spacer />
              <NumberInput
                id="txtMinQty"
                value={product.minimumOrderQuantity ?? undefined}
                label="Min Order Quantity"
                onChange={(value) =>
                  onChange(value, "minimumOrderQuantity", (input) => {
                    if (product.layerSize && product.layerSize > 0 && input) {
                      return (input % product.layerSize !== 0 ? `Minimum Order Quantity must be a multiple of the Layer Size (${product.layerSize}).` : null);
                    }
                    return null;
                })}
                step={product.layerSize ?? 1}
                error={validation.minimumOrderQuantity}
              ></NumberInput>
                {product.minimumOrderQuantity && product.layerSize ? (
                  <Alert severity="info">
                     <strong>{product.minimumOrderQuantity / product.layerSize}</strong>  
                     {" "} Layer(s)      
                  </Alert>
                ) : null}

            </FeatureCardColumn>
          </FeatureCardRow>
        </FeatureCard>
      </Body>
      <ButtonContainer>
        <Button
          id="btnCreate"
          label={product.id == 0 ? "Add" : "Update"}
          variant="contained"
          onClick={updateProduct}
        ></Button>
      </ButtonContainer>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  flex: 1;
`;

const Body = styled.div`
  flex: 1;
`;
const Active = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ButtonContainer = styled.div`
display:flex;
bottom:0;
margin-top:10px;
    justify-content: flex-end;

    button:first-of-type{
        margin-right:10px;
    }
}

`;

const StyledOption = styled.li`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0;
  }
`;

const OptionImage = styled.img`
  width: 40px;
  height: 40px;
  margin-right: 16px;
  border-radius: 4px;
`;

const OptionText = styled.div`
  display: flex;
  font-size: 0.8rem;
  flex-direction: column;
`;

const OptionName = styled.span`
  font-weight: bold;
  margin-bottom: 4px;
`;

const OptionSize = styled.span`
  color: #666;
`;

const MappedProduct = styled.div`
  margin-top: 20px;
`;


const Spacer = styled.div`
margin-bottom:8px;
`

const FieldHint = styled.small`
  display: block;
  margin-top: 4px;
  color: #555;
`;

const ReadOnlyValue = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 56px;
  justify-content: center;
`;

const ComparisonActionRow = styled.div`
  margin-top: 12px;
  width:100%;
  text-align:center;
`;

const ComparisonTooltip = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left}px`};
  z-index: 50;
  min-width: min(520px, 72vw);
  max-width: min(720px, 88vw);
  padding: 12px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid rgba(7, 94, 84, 0.16);
  box-shadow: 0 18px 40px rgba(7, 94, 84, 0.18);
`;

const ComparisonTooltipHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
`;

const ComparisonTooltipTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
`;

const ComparisonLoading = styled.div`
  padding: 18px 0;
  display: flex;
  justify-content: center;
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.8fr 0.75fr 1.2fr;
  gap: 8px;
`;

const ComparisonGridHeader = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #54656f;
  border-bottom: 1px solid rgba(7, 94, 84, 0.1);
  padding-bottom: 6px;
`;

const ComparisonGridRow = styled.div`
  display: contents;
`;

const ComparisonGridValue = styled.div<{ $isCurrentSupplier?: boolean }>`
  font-size: 0.82rem;
  line-height: 1.4;
  padding: 4px 0;
  border-bottom: 1px solid rgba(7, 94, 84, 0.06);
  font-weight: ${({ $isCurrentSupplier }) => ($isCurrentSupplier ? 700 : 400)};
  background: ${({ $isCurrentSupplier }) => ($isCurrentSupplier ? "rgba(37, 211, 102, 0.12)" : "transparent")};
  color: ${({ $isCurrentSupplier }) => ($isCurrentSupplier ? "#0b3f38" : "inherit")};
`;